/**
 * Generic React island that wraps an existing Rails-rendered `<select>`
 * (or `collection_select`) in an Alchemy `Select`. Used to migrate the
 * remaining `corejs-typeahead` call sites in legacy Rails views without
 * rewriting them as full SPAs.
 *
 * Mount-side: see `mountLegacyAlchemySelects.tsx`, which scans for
 * `.js-legacy-alchemy-select` wrappers and bootstraps this component.
 *
 * Behavior:
 * - Reads `<option>`s from the underlying `<select>` on mount.
 * - On change: writes back to `selectEl.value` and dispatches native
 *   `change` + `input` events so Rails form submission and existing jQuery
 *   handlers keep working unchanged.
 * - Listens for a `legacyAlchemySelect:sync` event on the underlying
 *   `<select>` so legacy code that mutates it directly (e.g. clearing a
 *   filter) can keep the Alchemy UI in sync.
 */
import { useEffect, useMemo, useState } from 'react';
import { Select } from '@m/alchemy-ui/Select';
import { AlchemyWrapper } from 'common/alchemyComponents/AlchemyWrapper';

interface LegacyAlchemySelectProps {
  selectEl: HTMLSelectElement;
  placeholder?: string;
  required?: boolean;
  hideBlankOption?: boolean;
  doNotTranslate?: boolean;
  /**
   * Label to display for an empty-value option that has no text in the
   * underlying `<select>` (e.g. `include_blank: true`). Defaults to an em
   * dash so the option is still visible and clickable inside the Alchemy
   * menu. Has no effect when `hideBlankOption` is true.
   */
  blankOptionLabel?: string;
}

interface OptionShape {
  value: string;
  label: string;
  labelString: string;
  doNotTranslate: boolean;
}

const LegacyAlchemySelect = ({
  selectEl,
  placeholder,
  required = false,
  hideBlankOption = false,
  doNotTranslate = false,
  blankOptionLabel = '—',
}: LegacyAlchemySelectProps) => {
  const options = useMemo<OptionShape[]>(() => {
    const all = Array.from(selectEl.options).map(opt => {
      const text = opt.textContent ?? '';
      const isEmptyPlaceholder = opt.value === '' && text.trim() === '';
      const label = isEmptyPlaceholder ? blankOptionLabel : text;
      return {
        value: opt.value,
        label,
        labelString: label,
        doNotTranslate,
      };
    });
    return hideBlankOption ? all.filter(o => o.value !== '') : all;
  }, [selectEl, hideBlankOption, doNotTranslate, blankOptionLabel]);

  const [value, setValue] = useState<string>(() => selectEl.value);

  // Keep the Alchemy UI in sync when legacy code mutates the underlying
  // `<select>` directly (it dispatches `legacyAlchemySelect:sync` afterwards).
  useEffect(() => {
    const syncFromSelect = () => setValue(selectEl.value);
    selectEl.addEventListener('legacyAlchemySelect:sync', syncFromSelect);
    return () => selectEl.removeEventListener('legacyAlchemySelect:sync', syncFromSelect);
  }, [selectEl]);

  const handleChange = (option?: OptionShape) => {
    if (!option) return;
    const nextValue = option.value;
    setValue(nextValue);
    selectEl.value = nextValue;
    selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    selectEl.dispatchEvent(new Event('input', { bubbles: true }));
  };

  return (
    <AlchemyWrapper>
      <Select
        isSearchable
        isFullWidth
        required={required}
        options={options}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
      />
    </AlchemyWrapper>
  );
};

export default LegacyAlchemySelect;
